// Thimbles — cups-and-ball shell game, BaaliGames edition.
//
// ⚠️ UI-ONLY PREVIEW (no backend, no real money). Self-contained client-side
// mock so the look + feel can be reviewed before any wallet/engine work.
//
// VISUALS: composited from the real rendered art the user supplied in
// public/games/thimbles/ — background.png, board.png, cup.png, ball.png.
// The cups are positioned ON the board's velvet via the LAYOUT constants
// below (easy to nudge: SLOT_X / CUP_BASE_Y / CUP_W / BALL_W / LIFT_Y).
//
// MODES: SINGLE ball — 1 of 3 wins (1/3) → BIG multiplier.
//        DOUBLE ball — 2 of 3 win (2/3) → smaller multiplier.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import Icon from '@baaligames/shared-ui/components/Icon';
import GameSettingsMenu from '@baaligames/shared-ui/components/GameSettingsMenu';
import GameLoadingScreen from '@baaligames/shared-ui/components/GameLoadingScreen';
import { useAssetPreloader } from '@baaligames/shared-ui/lib/useAssetPreloader';
import { api } from '@baaligames/shared-ui/lib/api';
import '../ludo/ludo.css'; // shared portrait shell (.app-container, .ludo-root centering, safe-area)

// ── Server responses (the backend is the single source of truth for money,
//    outcome, and shuffle speed — the client only ANIMATES what it returns) ──
type PlayResponse = {
  roundId: number; mode: Mode; bet: number; multiplier: number;
  peakMs: number; deadlineAt: string; newBalance: number; potentialWin: number;
  pickTimeoutSeconds: number;
};
type PickResponse = {
  roundId: number; isWin: boolean; mode: Mode; bet: number; payout: number; net: number;
  multiplier: number; pickedCup: number; ballCup: number; ballCups: number[];
  status: string; forfeited: boolean; newBalance: number;
};
type OpenRound = { roundId: number; mode: Mode; bet: number; multiplier: number; peakMs: number; secondsLeft: number };
type StateResponse = {
  enabled: boolean; maintenanceMessage?: string; wallet: number;
  multipliers: Record<Mode, number>; bet: { min: number; max: number; presets: number[] };
  pickTimeoutSeconds: number; openRound: OpenRound | null;
};

const ART = '/games/thimbles';
// NOTE: background.png is intentionally NOT used — the backdrop is generated in
// CSS below (radial "wall" on top, linear "carpet" + spotlight pool on the
// bottom). That removes a ~3.6 MB image load (instant, no flash) and lets the
// wall/carpet horizon stay locked to the board on every screen size.
// Only the board + cup are images now; the ball is drawn in SVG (Ball below).
const IMG = {
  board: `${ART}/board.png`, cup: `${ART}/cup.png`,
  // top-bar assets (same coin/plus/settings convention as the other games)
  settings: `${ART}/Settings.png`, coin: `${ART}/Coin.png`, plus: `${ART}/Plus.png`,
};

// Everything the game needs before the first frame should look right: every
// image (board, cup, top-bar icons) + the logo the loader itself shows. Stable
// module-scope array so the preloader doesn't restart each render. (Background +
// ball are CSS/SVG, and sounds are synthesised WebAudio — nothing to fetch.)
const PRELOAD_URLS: readonly string[] = [
  IMG.board, IMG.cup, IMG.settings, IMG.coin, IMG.plus, `${ART}/Logo.png`,
];

// ── LAYOUT (fractions of the board element — tweak freely to seat the cups) ─
// Calibrated from a center-column scan of board.png: the blue velvet TOP
// surface runs y≈0.07 (back) → 0.36 (front edge); below that is the gold
// frame + front face. So cups must sit with their BASE at ~0.35 (front of
// the velvet), not on the front face.
const BOARD_AR = 1900 / 560;        // board.png native aspect ratio
const SLOT_X   = [0.285, 0.50, 0.715]; // cup-centre x per slot (fraction of board width)
const CUP_BASE_Y = 0.335;           // cup BASE y from board top (seated on the velvet, centred)
const CUP_W    = 0.178;             // cup width (fraction of board width)
const BALL_W   = 0.092;             // ball width (fraction of board width)
const LIFT_Y   = '-66%';            // how far a cup rises to reveal (fraction of cup height)

// ── game tunables (display-only for now) ──
const CUPS = 3;
const MULT = { single: 2.94, double: 1.47 };
const PRESETS = [50, 100, 250, 500, 1000];
const MIN_BET = 50;
const DEMO_START = 10000;

// ════════════════════════════════════════════════════════════════════════════
// HOUSE CONTROL — the result is decided by the HOUSE, not by which cup the
// player taps. This is the core money-safety rule for the whole game.
//
// WHY THIS EXISTS (read before building the backend):
//   In a fair shell game the player can, with enough practice or a slow-motion
//   camera, learn to follow the ball and win ~1/3 (single) or ~2/3 (double) of
//   the time — which, against our payout multipliers, would BLEED THE HOUSE.
//   So the game must NOT be "find the real ball". Instead:
//     1. The ball is NEVER committed to a physical cup during the round (see
//        the play()/pick() flow — `ballUnderIds` is empty all through shuffle
//        + picking, so the ball literally isn't in the DOM and can't be traced
//        by a slow-mo camera OR by inspecting/clearing cups in dev-tools).
//     2. When the player taps, the HOUSE decides win/lose first, and only THEN
//        do we place the ball under the tapped cup (win) or another cup (loss).
//        The player can never prove the ball "was really" anywhere else.
//
// COOL-OFF (the "if they win too much, make them lose" rule the product owner
// asked for): every consecutive win raises the player's hot-streak; once they
// hit `streakCoolAt` wins in a row we multiply their win chance by
// `streakCoolFactor` (a big cut) so a lucky run is reined back toward the house
// edge. Any loss (or a timeout forfeit) resets the streak to 0.
//
// ⚠️ THESE NUMBERS ARE DEMO PLACEHOLDERS AND THIS LOGIC IS CLIENT-SIDE ONLY.
//    A client can be patched, so for REAL MONEY this entire decision MUST move
//    SERVER-SIDE into an RTP-proven engine (same pattern as Lucky 7: a pure
//    result engine + a Monte-Carlo house-safety gate + per-user engagement
//    caps). The client should then only ANIMATE a result the server returns —
//    never compute win/lose itself. The backend version should also layer in:
//    real RTP targeting per mode, daily/however-long win caps per user, a
//    short "engagement" allowance for brand-new players, and admin-tunable
//    knobs — not just this single streak cut.
// ════════════════════════════════════════════════════════════════════════════
const HOUSE = {
  // base probability the house LETS the player win, per mode. Deliberately
  // below the fair 1/3 (0.333) and 2/3 (0.667) so the house keeps an edge even
  // before the cool-off kicks in.
  baseWin:          { single: 0.34, double: 0.66 },
  streakCoolAt:     2,     // after this many wins in a row, start cooling off
  streakCoolFactor: 0.22,  // multiply win chance by this while the player is "hot"
  // POST-MAX cheat escalation: once the player has won enough to max out the
  // shuffle speed (SPEED.winsToMax below), they've "beaten" the difficulty
  // ramp — so from then on every further win compounds an extra squeeze on the
  // win chance. This is the "now we start cheating" phase the product owner
  // asked for: the house forces the ball away from their pick more and more.
  postMaxSqueeze:   0.7,   // each win past the speed cap multiplies win chance by this
  minWinChance:     0.05,  // never drop to literally 0 (keeps it from feeling obviously rigged)
};

// ════════════════════════════════════════════════════════════════════════════
// ADAPTIVE SHUFFLE SPEED — the cups shuffle FASTER the more the player wins.
//
// WHY (read before building the backend): a brand-new player should get a
// brisk-but-followable shuffle (so the game feels fair and learnable), NOT the
// blink-and-you-miss-it burst. Every WIN proves a little more skill, so we
// shave the peak speed up a notch for their next round — making the ball
// progressively harder to track. We RAMP UP TO, but NEVER EXCEED, the fastest
// burst we already use (`maxPeakMs`); that current max stays the absolute
// ceiling. Once the player has won `winsToMax` times the speed is pinned at the
// ceiling, and any further winning triggers the HOUSE cheat escalation above.
//
// The level is driven by `winsForSpeed` (cumulative wins this session). It does
// NOT reset on a loss — once a player has shown they can win N times, the game
// stays hard for them (a loss shouldn't hand the difficulty back).
//
// ⚠️ DEMO + CLIENT-SIDE. For real money this difficulty curve + the cheat MUST
//    live SERVER-SIDE: the backend tracks the player's win history and returns
//    BOTH the shuffle speed to animate AND the (already-decided) result. The
//    client must never compute either — otherwise a player could patch the
//    frontend to force a slow shuffle and/or a win.
// ════════════════════════════════════════════════════════════════════════════
const SPEED = {
  startPeakMs: 95,   // new-user PEAK delay (ms). SMALLER = faster. Brisk but trackable.
  maxPeakMs:   42,   // the ceiling = our current fastest burst. We never go below this.
  winsToMax:   6,    // wins needed to ramp the peak from startPeakMs → maxPeakMs.
};

// Peak shuffle delay (ms) for a given cumulative win count: linearly shaves
// startPeakMs → maxPeakMs across the first `winsToMax` wins, then holds at the
// ceiling. SMALLER delay = faster shuffle.
function peakForWins(wins: number): number {
  const t = Math.min(1, Math.max(0, wins) / SPEED.winsToMax);
  return Math.round(SPEED.startPeakMs + (SPEED.maxPeakMs - SPEED.startPeakMs) * t);
}
const BET_TIMEOUT = 45;             // seconds to pick a cup once the bet is placed

type Mode = 'single' | 'double';
type Phase = 'idle' | 'placing' | 'shuffling' | 'picking' | 'revealing' | 'result';
// `lane` gives the cross its DEPTH: while two cups swap, one rides the 'front'
// lane (nearer the viewer — lower, larger, on top) and the other the 'back'
// lane (further — higher, smaller, behind), so they arc AROUND each other
// instead of sliding straight through. 'rest' = settled flat on the velvet.
type Lane = 'front' | 'back' | 'rest';
type Cup = { id: number; slot: number; lane: Lane };

// ── tiny guarded WebAudio cues (no assets) ──
const Sfx = (() => {
  let ctx: AudioContext | null = null;
  let noiseBuf: AudioBuffer | null = null;
  const on = { v: true };
  const ac = () => { if (!on.v) return null; try { ctx ||= new (window.AudioContext || (window as any).webkitAudioContext)(); return ctx; } catch { return null; } };
  const tone = (f: number, t: number, type: OscillatorType = 'sine', g = 0.05) => {
    const c = ac(); if (!c) return;
    try { const o = c.createOscillator(), gain = c.createGain(); o.type = type; o.frequency.value = f;
      gain.gain.setValueAtTime(g, c.currentTime); gain.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + t);
      o.connect(gain); gain.connect(c.destination); o.start(); o.stop(c.currentTime + t); } catch { /* ignore */ }
  };
  const getNoise = (c: AudioContext) => {
    if (noiseBuf) return noiseBuf;
    const len = Math.floor(c.sampleRate * 0.5);
    const buf = c.createBuffer(1, len, c.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    noiseBuf = buf; return buf;
  };
  // friction "shhf" of a cup dragged across velvet: a band-limited noise burst
  // that swells then fades, with a tiny pitch glide so it reads as a slide.
  const drag = (dur: number, g: number) => {
    const c = ac(); if (!c) return;
    try {
      const src = c.createBufferSource(); src.buffer = getNoise(c); src.loop = true;
      src.playbackRate.value = 0.85 + Math.random() * 0.3;
      const hp = c.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 320;
      const bp = c.createBiquadFilter(); bp.type = 'bandpass'; bp.Q.value = 0.8;
      const lp = c.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 2700;
      const gain = c.createGain();
      const t = c.currentTime;
      const f0 = 850 + Math.random() * 500;
      bp.frequency.setValueAtTime(f0, t);                                   // a slight upward glide = the cup
      bp.frequency.linearRampToValueAtTime(f0 + 350, t + dur);             // accelerating across the felt
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(g, t + dur * 0.32);            // quick scrape-in
      gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);             // settle
      src.connect(hp); hp.connect(bp); bp.connect(lp); lp.connect(gain); gain.connect(c.destination);
      src.start(t); src.stop(t + dur + 0.03);
    } catch { /* ignore */ }
  };
  return {
    setOn: (v: boolean) => { on.v = v; },
    tap: () => tone(440, 0.08, 'triangle', 0.06),
    // dur scales with how fast THIS swap is (fast burst = short scrape, slow = longer drag)
    swap: (ms = 150) => drag(Math.min(0.17, Math.max(0.05, (ms / 1000) * 0.85)), 0.06),
    lift: () => tone(680, 0.12, 'sine', 0.05),
    win: () => [523, 659, 784, 1047].forEach((f, i) => setTimeout(() => tone(f, 0.22, 'triangle', 0.06), i * 90)),
    lose: () => { tone(200, 0.25, 'sawtooth', 0.05); setTimeout(() => tone(150, 0.3, 'sawtooth', 0.04), 120); },
  };
})();

// Glossy red ball — pure SVG (no image load). Photo-real per the reference:
//   • light from the UPPER-LEFT → bright soft highlight there
//   • red brightens toward the top, deepens down the body
//   • the BOTTOM third falls into a near-black CORE shadow (light can't wrap
//     under the sphere) — with a faint warm bounce just inside the rim so it
//     reads as 3-D, not flat black
//   • a soft CAST shadow on the ground, offset lower-right (opposite the light)
function Ball() {
  return (
    <svg viewBox="0 0 100 100" className="block w-full h-auto" aria-hidden="true">
      <defs>
        {/* body — radial lit from upper-left, falling to a deep maroon at the
            lower rim. Drawn ON the sphere circle so it is clipped to the
            silhouette — nothing can bleed outside the edge. */}
        <radialGradient id="tb_body" cx="0.38" cy="0.29" r="0.98">
          <stop offset="0"    stopColor="#ff8273" />
          <stop offset="0.10" stopColor="#f0463a" />
          <stop offset="0.27" stopColor="#d4222a" />
          <stop offset="0.46" stopColor="#9e1116" />
          <stop offset="0.64" stopColor="#62090e" />
          <stop offset="0.82" stopColor="#310509" />
          <stop offset="1"    stopColor="#0e0103" />
        </radialGradient>
        {/* core shadow — deepens ONLY the underside the light can't reach to a
            near-black. Same circle as the sphere, so it stays inside the edge. */}
        <radialGradient id="tb_core" cx="0.5" cy="0.95" r="0.66">
          <stop offset="0"    stopColor="rgba(3,0,1,0.88)" />
          <stop offset="0.45" stopColor="rgba(6,0,1,0.36)" />
          <stop offset="0.8"  stopColor="rgba(6,0,1,0)" />
        </radialGradient>
        {/* soft specular glow (upper-left) */}
        <radialGradient id="tb_spec" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0"    stopColor="rgba(255,252,250,0.95)" />
          <stop offset="0.5"  stopColor="rgba(255,226,218,0.34)" />
          <stop offset="1"    stopColor="rgba(255,215,205,0)" />
        </radialGradient>
        {/* ground contact shadow */}
        <radialGradient id="tb_cast" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0"    stopColor="rgba(0,0,0,0.55)" />
          <stop offset="0.5"  stopColor="rgba(0,0,0,0.28)" />
          <stop offset="1"    stopColor="rgba(0,0,0,0)" />
        </radialGradient>
      </defs>

      {/* contact shadow — wide soft pool + a tight dark core hugging the base,
          so the ball reads as resting on the surface (not floating). */}
      <ellipse cx="50" cy="85" rx="33" ry="6" fill="url(#tb_cast)" />
      <ellipse cx="50" cy="84.5" rx="18" ry="3.2" fill="rgba(0,0,0,0.5)" />

      {/* sphere */}
      <circle cx="50" cy="44" r="40" fill="url(#tb_body)" />
      <circle cx="50" cy="44" r="40" fill="url(#tb_core)" />

      {/* specular highlight — a SOFT sheen with a gentle (not hard) core */}
      <ellipse cx="38" cy="26" rx="15" ry="11" fill="url(#tb_spec)" transform="rotate(-22 38 26)" />
      <ellipse cx="35" cy="23" rx="4.5" ry="2.8" fill="rgba(255,255,255,0.6)" transform="rotate(-22 35 23)" />
    </svg>
  );
}

// Casino-chip palette — each stake is a colour-coded poker chip (instant read,
// proper game feel). Jewel tones that pop on the purple velvet.
type ChipSkin = { edge: string; face: string; ink: string };
const CHIP_THEME: Record<number, ChipSkin> = {
  50:   { edge: '#e8505b', face: '#b3232e', ink: '#fff' },   // red
  100:  { edge: '#4583e3', face: '#27509e', ink: '#fff' },   // blue
  250:  { edge: '#36b475', face: '#1c7a4b', ink: '#fff' },   // green
  500:  { edge: '#9a63e6', face: '#5f33a6', ink: '#fff' },   // violet
  1000: { edge: '#f0c24a', face: '#8a5e0c', ink: '#fff7df' },// gold
};
// Fallback palette so ANY preset amount gets a chip skin (admin can set any
// bet presets without crashing the page). Known amounts keep their colour;
// unknown ones cycle this palette by position.
const CHIP_PALETTE: ChipSkin[] = [
  { edge: '#e8505b', face: '#b3232e', ink: '#fff' },     // red
  { edge: '#4583e3', face: '#27509e', ink: '#fff' },     // blue
  { edge: '#36b475', face: '#1c7a4b', ink: '#fff' },     // green
  { edge: '#9a63e6', face: '#5f33a6', ink: '#fff' },     // violet
  { edge: '#f0c24a', face: '#8a5e0c', ink: '#fff7df' },  // gold
  { edge: '#e07b39', face: '#9a4d16', ink: '#fff' },     // orange
];
const chipSkin = (amount: number, idx: number): ChipSkin =>
  CHIP_THEME[amount] || CHIP_PALETTE[idx % CHIP_PALETTE.length];

// Build a FRESH shuffle-speed envelope every round so the rhythm is never the
// same twice (a fixed cadence is predictable → trackable). Guarantees:
//   • the first swap is always the slowest (slow start)
//   • the last swap is always the slowest (slow end)
//   • at least one swap in the middle hits true MAX speed (untraceable burst)
// Everything else — swap count, where the peak sits, how sharp the ramp is,
// whether there's a single peak or a fast-slow-fast double burst — varies.
// `fastMs` = the PEAK (fastest) delay for THIS round — driven by the player's
// adaptive speed level (see SPEED / peakForWins). New players get a larger
// (slower) peak; it shrinks toward SPEED.maxPeakMs as they win. Everything is
// clamped to never go faster than `fastMs`, so the level is a hard ceiling.
function buildShuffleDelays(fastMs: number = SPEED.maxPeakMs): number[] {
  const SLOW = 300;            // ms — start/end pace (always slow, regardless of level)
  const FAST = fastMs;         // ms — peak (fastest) speed THIS round
  const n = 13 + Math.floor(Math.random() * 7);      // 13..19 swaps
  const peak = 0.40 + Math.random() * 0.24;          // max-speed sits 40%..64% in
  const sharp = 1.5 + Math.random() * 1.6;           // ramp steepness
  const doubleBurst = Math.random() < 0.45;          // sometimes accelerate twice
  const dip = Math.min(0.85, peak + 0.16 + Math.random() * 0.10);
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1);                            // 0..1 across the shuffle
    // U-shaped base: 0 at the peak (fastest), 1 at the ends (slowest)
    let u = Math.abs(t - peak) / Math.max(peak, 1 - peak);
    let delay = FAST + (SLOW - FAST) * Math.pow(u, sharp);
    if (doubleBurst && t > peak) {                    // a 2nd acceleration later on
      const u2 = Math.abs(t - dip) / Math.max(dip, 1 - dip);
      delay = Math.min(delay, FAST + (SLOW - FAST) * Math.pow(u2, sharp));
    }
    delay *= 0.90 + Math.random() * 0.18;             // organic jitter, no two alike
    out.push(Math.max(FAST, Math.round(delay)));      // clamp: never faster than this level's peak
  }
  out[0] = SLOW; out[n - 1] = SLOW;                   // hard-lock slow start & end
  let lo = 1; for (let i = 1; i < n - 1; i++) if (out[i] < out[lo]) lo = i;
  out[lo] = FAST;                                     // hard-lock one true peak-speed burst
  return out;
}

export default function ThimblesPage() {
  const navigate = useNavigate();
  // shared loading screen: wait for every image AND the display fonts before
  // revealing the game, so nothing pops in / reflows on a cold load.
  const assetStatus = useAssetPreloader(PRELOAD_URLS, 700);
  const [fontsReady, setFontsReady] = useState(false);
  const loading = !assetStatus.ready || !fontsReady;

  const [phase, setPhase] = useState<Phase>('idle');
  // Remember the last-used mode + stake across sessions (2026-08-06 UX sweep)
  // so a returning player is one tap from their usual bet.
  const [mode, setMode] = useState<Mode>(() => {
    const s = typeof localStorage !== 'undefined' ? localStorage.getItem('thimbles.mode') : null;
    return (s === 'single' || s === 'double') ? (s as Mode) : 'single';
  });
  const [bet, setBet] = useState(() => {
    const n = typeof localStorage !== 'undefined' ? Number(localStorage.getItem('thimbles.bet')) : NaN;
    return Number.isFinite(n) && n > 0 ? n : 100;
  });
  const [wallet, setWallet] = useState(DEMO_START);
  const [soundOn, setSoundOn] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Persist mode + stake so the next visit restores them.
  useEffect(() => {
    try { localStorage.setItem('thimbles.mode', mode); localStorage.setItem('thimbles.bet', String(bet)); } catch { /* private mode */ }
  }, [mode, bet]);
  // Server-driven config (overrides the local display defaults on mount). The
  // SERVER enforces money/outcome regardless; these just keep the UI in sync.
  const [cfg, setCfg] = useState<{ multipliers: Record<Mode, number>; presets: number[]; min: number; max: number; pickTimeoutSeconds: number }>(
    { multipliers: MULT, presets: PRESETS, min: MIN_BET, max: 5000, pickTimeoutSeconds: BET_TIMEOUT });
  const [notice, setNotice] = useState<string | null>(null);   // transient error/maintenance toast
  const roundIdRef = useRef<number | null>(null);              // the open server round id (sent to /pick)
  // Pull wallet + live config from the server on mount.
  useEffect(() => {
    let alive = true;
    api.get<StateResponse>('/thimbles/state').then((s) => {
      if (!alive || !s) return;
      if (s.enabled === false) { setNotice(s.maintenanceMessage || 'Thimbles is unavailable right now.'); return; }
      setWallet(s.wallet);
      setCfg({ multipliers: s.multipliers, presets: s.bet.presets, min: s.bet.min, max: s.bet.max, pickTimeoutSeconds: s.pickTimeoutSeconds });
      // Resume an IN-PROGRESS round (player refreshed / relaunched mid-round) so
      // the already-paid bet isn't orphaned: restore the round id, drop straight
      // to the pick phase, and resume the countdown from the server's remaining
      // seconds. (If <2s left, skip — the sweeper worker will forfeit it.)
      if (s.openRound && s.openRound.secondsLeft > 1) {
        const o = s.openRound;
        roundIdRef.current = o.roundId;
        roundRef.current = true; actedRef.current = false;
        setMode(o.mode); setBet(o.bet);
        setCups(Array.from({ length: CUPS }, (_, i) => ({ id: i, slot: i, lane: 'rest' as const })));
        setPhase('picking');
        startTimer(o.secondsLeft);
      }
    }).catch(() => { /* keep defaults if the call fails (e.g. locked + no dev-pin) */ });
    return () => { alive = false; };
  }, []);
  // Auto-dismiss the transient notice toast.
  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 3200);
    return () => clearTimeout(t);
  }, [notice]);

  const [cups, setCups] = useState<Cup[]>(() => Array.from({ length: CUPS }, (_, i) => ({ id: i, slot: i, lane: 'rest' as const })));
  // ballUnderIds = which cups currently have the ball RENDERED IN THE DOM. It
  // is EMPTY during shuffling + picking, so the ball literally is not on the
  // page then — deleting/clearing the cups in dev-tools reveals nothing. The
  // ball is only injected (a) for the brief "watch" reveal and (b) at the
  // final reveal, where the OUTCOME we chose decides which cup it sits under.
  const [ballUnderIds, setBallUnderIds] = useState<Set<number>>(new Set());
  const [liftedIds, setLiftedIds] = useState<Set<number>>(new Set());
  const [swapMs, setSwapMs] = useState(220);    // current swap duration → drives the arc tween
  const [tick, setTick] = useState(0);          // swap counter → guarantees each arc replays
  const [pickedId, setPickedId] = useState<number | null>(null);
  const [won, setWon] = useState(false);
  const [payout, setPayout] = useState(0);
  const [shownPayout, setShownPayout] = useState(0);       // animated count-up on the win screen
  const [timeLeft, setTimeLeft] = useState(BET_TIMEOUT);   // bet-window countdown
  const [timedOut, setTimedOut] = useState(false);         // round ended by timeout (forfeit)
  // NOTE: the win/lose decision, the cool-off/cheat, and the shuffle speed are
  // now ALL decided SERVER-SIDE (shared/src/games/thimbles/engine.js +
  // model.settlePick). The client only animates the server's verdict + peakMs,
  // so the old client-side winStreak/winsForSpeed counters are gone. The HOUSE
  // and SPEED blocks higher up are kept only as a readable description of the
  // (now server-side) house logic.
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const roundRef = useRef(false);       // a paid round is in progress (bet placed, not yet settled)
  const actedRef = useRef(false);       // the player has picked (or forfeited) this round

  const cancelled = useRef(false);
  const clearTimer = useCallback(() => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } }, []);
  useEffect(() => { Sfx.setOn(soundOn); }, [soundOn]);
  useEffect(() => () => { cancelled.current = true; clearTimer(); }, [clearTimer]);
  // full-screen takeover: hide the site nav while in-game (same as other games)
  useEffect(() => { document.body.classList.add('thimbles-active'); return () => document.body.classList.remove('thimbles-active'); }, []);

  // wait for the display fonts (Cinzel/Poppins) so the title doesn't flash in a
  // system font, with a hard safety cap so a slow font CDN never pins the loader.
  useEffect(() => {
    let alive = true; const done = () => { if (alive) setFontsReady(true); };
    const fonts = (document as any).fonts;
    if (fonts?.ready?.then) fonts.ready.then(done); else done();
    const t = setTimeout(done, 2500);
    return () => { alive = false; clearTimeout(t); };
  }, []);

  // win screen: count the payout up from 0 for a satisfying climb
  useEffect(() => {
    if (phase === 'result' && won) {
      let raf = 0; const start = performance.now(); const dur = 950;
      const tick = (now: number) => {
        const p = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - p, 3);
        setShownPayout(Math.round(payout * eased));
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(raf);
    }
    setShownPayout(0);
  }, [phase, won, payout]);

  const mult = cfg.multipliers[mode] ?? MULT[mode];
  const winChips = useMemo(() => Math.floor(bet * mult), [bet, mult]);
  const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
  const setSlot = (id: number, slot: number) => setCups((cs) => cs.map((c) => (c.id === id ? { ...c, slot } : c)));

  const status = {
    idle: 'Place your bet & find the ball',
    placing: mode === 'single' ? 'Remember where the ball is…' : 'Remember both balls…',
    shuffling: 'Shuffling…',
    picking: 'Pick a cup!',
    revealing: '…',
    result: won ? 'You found it! 🎉' : 'So close — try again!',
  }[phase];

  // ── 45-second bet window. The moment the player pays, this covers the
  //    controls and counts down. If it hits 0 before a cup is picked, the
  //    round is FORFEITED (bet already deducted → it's simply a loss). ──
  const forfeit = useCallback(() => {
    if (!roundRef.current || actedRef.current) return;
    actedRef.current = true;
    clearTimer();
    cancelled.current = true;                       // halt any in-flight placing/shuffle loop
    setLiftedIds(new Set()); setBallUnderIds(new Set()); setPickedId(null);
    // The bet was already debited server-side; the sweeper worker forfeits the
    // round (a loss — the house keeps it) within ~1s. We just show the
    // time's-up result and re-sync the authoritative balance from the server.
    api.get<StateResponse>('/thimbles/state').then((s) => { if (s) setWallet(s.wallet); }).catch(() => {});
    setTimedOut(true); setWon(false); setPayout(0); Sfx.lose();
    setPhase('result');
    setTimeout(() => {
      setTimedOut(false);
      setCups(Array.from({ length: CUPS }, (_, i) => ({ id: i, slot: i, lane: 'rest' as const })));
      roundRef.current = false; roundIdRef.current = null;
      setPhase('idle');
    }, 2200);
  }, [clearTimer]);

  // `fromSeconds` lets a resumed (refreshed mid-round) round start its countdown
  // from the server's remaining seconds rather than the full window.
  const startTimer = useCallback((fromSeconds?: number) => {
    clearTimer();
    setTimeLeft(fromSeconds ?? cfg.pickTimeoutSeconds);
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => { if (t <= 1) { forfeit(); return 0; } return t - 1; });
    }, 1000);
  }, [clearTimer, forfeit, cfg.pickTimeoutSeconds]);

  const play = useCallback(async () => {
    if (phase !== 'idle' || bet < cfg.min || bet > cfg.max || bet > wallet) return;
    cancelled.current = false; Sfx.tap();
    setPickedId(null); setWon(false); setPayout(0); setTimedOut(false); setNotice(null);

    // 1) Place the bet on the SERVER (atomic debit). The server returns the
    //    round id, the authoritative new balance, and the shuffle speed (peakMs)
    //    — the client never decides money/outcome/speed, it only ANIMATES.
    let res: PlayResponse;
    try {
      res = await api.post<PlayResponse>('/thimbles/play', { mode, bet });
    } catch (e) {
      setNotice((e as { message?: string })?.message || 'Could not place bet.');
      Sfx.lose();
      return;
    }
    roundIdRef.current = res.roundId;
    setWallet(res.newBalance);
    roundRef.current = true; actedRef.current = false;
    startTimer();                                    // start the pick countdown the instant the bet is placed

    setCups(Array.from({ length: CUPS }, (_, i) => ({ id: i, slot: i, lane: 'rest' as const })));   // reset to a row

    // 1) "Watch the ball" — show it under random cup(s) PURELY for the illusion.
    //    This placement does NOT decide the result (that's chosen at pick).
    //    The ball stays BEHIND the cup; we let the cup physically DROP over it,
    //    then remove it from the DOM — so it never fades away in mid-air.
    const ballCount = mode === 'single' ? 1 : 2;
    const showIds = [0, 1, 2].sort(() => Math.random() - 0.5).slice(0, ballCount);
    setPhase('placing'); await sleep(200);
    setBallUnderIds(new Set(showIds)); setLiftedIds(new Set(showIds)); Sfx.lift();
    await sleep(1150);
    if (cancelled.current) return;
    setLiftedIds(new Set());            // cup drops OVER the ball (covers it)
    await sleep(430);                   // …wait until it's fully covered…
    setBallUnderIds(new Set());         // …THEN remove the ball from the page
    await sleep(110);

    // 2) Shuffle — NO ball in the DOM. Slow ramp → an UNTRACEABLE fast burst →
    //    settle. The cups are the same image, so even the burst gives no tell
    //    of which cup went where (and there's no ball committed to any of them).
    setPhase('shuffling');
    // Each swap = the two cups SWEEP AROUND each other on a synced arc (front
    // cup curves forward-and-down, back cup curves up-and-behind, the dip
    // peaking right as they cross). Duration-synced tweens — NO springs — so
    // consecutive fast swaps chain smoothly instead of jittering/overshooting.
    // The shuffle PEAK speed is decided by the SERVER (res.peakMs) from the
    // player's server-tracked win history — the client only builds the random
    // envelope around it and animates. (Speed has no money/anti-cheat effect:
    // the ball is never in the DOM during the shuffle, so it can't be tracked.)
    const delays = buildShuffleDelays(res.peakMs);
    for (let s = 0; s < delays.length; s++) {
      if (cancelled.current) return;
      const a = Math.floor(Math.random() * CUPS); let b = Math.floor(Math.random() * CUPS); if (b === a) b = (a + 1) % CUPS;
      const aFront = Math.random() < 0.5;                  // randomise who crosses in front
      setSwapMs(delays[s]); setTick((t) => t + 1);
      setCups((prev) => {
        const ca = prev.find((c) => c.slot === a)!, cb = prev.find((c) => c.slot === b)!;
        return prev.map((c) =>
          c.id === ca.id ? { ...c, slot: b, lane: (aFront ? 'front' : 'back') as Lane }
            : c.id === cb.id ? { ...c, slot: a, lane: (aFront ? 'back' : 'front') as Lane }
              : { ...c, lane: 'rest' as Lane });
      });
      Sfx.swap(delays[s]); await sleep(delays[s]);
    }
    setSwapMs(300);
    setCups((prev) => prev.map((c) => ({ ...c, lane: 'rest' as Lane })));   // settle flat
    await sleep(320);
    if (cancelled.current) return;
    setPhase('picking');
  }, [phase, bet, wallet, mode, cfg, startTimer]);

  // Reset cups + flags back to the idle lobby state.
  const resetToIdle = useCallback(() => {
    setLiftedIds(new Set()); setBallUnderIds(new Set()); setPickedId(null);
    setCups(Array.from({ length: CUPS }, (_, i) => ({ id: i, slot: i, lane: 'rest' as const })));
    roundRef.current = false; roundIdRef.current = null;
    setPhase('idle');
  }, []);

  const pick = useCallback(async (cup: Cup) => {
    if (phase !== 'picking' || actedRef.current) return;
    actedRef.current = true; clearTimer();          // player acted in time → stop the countdown
    Sfx.tap(); setPickedId(cup.id); setPhase('revealing');

    // ── The SERVER decides win/lose (house-controlled) and tells us WHICH cups
    //    reveal a ball (the player's cup on a win, another cup on a loss). The
    //    ball was never committed, so a loss simply renders it under a different
    //    cup — the player has no proof. The client only ANIMATES the verdict;
    //    it cannot influence money or outcome (the full house logic — base RTP,
    //    cool-off, the speed-driven cheat, the 7-day cap — lives server-side in
    //    shared/src/games/thimbles/engine.js + model.settlePick). ──
    let res: PickResponse;
    try {
      res = await api.post<PickResponse>('/thimbles/pick', { roundId: roundIdRef.current, cup: cup.id });
    } catch (e) {
      // Most likely a pick that arrived after the server deadline → the round
      // was (or will be) forfeited server-side. Show the time's-up result and
      // re-sync the wallet from the server.
      setNotice((e as { message?: string })?.message || 'Round closed.');
      api.get<StateResponse>('/thimbles/state').then((s) => { if (s) setWallet(s.wallet); }).catch(() => {});
      setWon(false); setTimedOut(true); setPhase('result');
      await sleep(2100);
      if (cancelled.current) return;
      resetToIdle();
      return;
    }

    const isWin = res.isWin;
    const ballCups = res.ballCups;   // server-authoritative cup index(es) holding a ball

    await sleep(150);
    setBallUnderIds(new Set(ballCups));   // inject the ball NOW (behind the cups), matching the verdict
    setLiftedIds(new Set([cup.id]));      // lift the picked cup
    Sfx.lift();
    await sleep(720);
    if (cancelled.current) return;
    if (!isWin) { setLiftedIds(new Set([cup.id, ...ballCups])); await sleep(560); } // show where it really was
    setWon(isWin);
    setWallet(res.newBalance);            // authoritative post-settle balance
    if (isWin) { setPayout(res.payout); Sfx.win(); } else { Sfx.lose(); }
    setPhase('result');
    await sleep(isWin ? 2700 : 2100);
    if (cancelled.current) return;
    resetToIdle();
  }, [phase, clearTimer, resetToIdle]);

  const busy = phase !== 'idle';
  const canPlay = phase === 'idle' && bet >= cfg.min && bet <= cfg.max && bet <= wallet;
  const showTimer = phase === 'placing' || phase === 'shuffling' || phase === 'picking';
  const urgent = timeLeft <= 10;
  const timerMsg = phase === 'picking' ? 'Pick a cup — quick!' : phase === 'shuffling' ? 'Keep your eye on the ball…' : 'Watch carefully…';
  // phase-aware HUD marquee text + icon (the strip under the top bar)
  const hud = {
    idle:      { t: 'PLACE YOUR BET', i: 'casino' },
    placing:   { t: mode === 'single' ? 'WATCH THE BALL' : 'WATCH BOTH BALLS', i: 'visibility' },
    shuffling: { t: 'SHUFFLING…', i: 'cached' },
    picking:   { t: 'PICK A CUP!', i: 'touch_app' },
    revealing: { t: 'REVEALING…', i: 'hourglass_top' },
    result:    { t: won ? 'YOU WON!' : 'ROUND OVER', i: 'emoji_events' },
  }[phase];

  return (
    <div className="thimbles-root ludo-root fixed inset-0 z-[9999]">
      {/* themed splash — same shared loader every game uses, with the Thimbles
          palette (plum + gold). Stays up until all art + fonts are loaded. */}
      <GameLoadingScreen visible={loading} theme="thimbles" progress={assetStatus.progress} subtext="shuffling the cups…" />

      {/* shared settings panel — same affordance every game offers (Ludo / SL /
          Plinko / Lucky7 …). Thimbles is single-player, so only the Sound toggle
          is active; mic/listen rows are hidden via voiceEnabled={false}. */}
      <GameSettingsMenu
        open={settingsOpen}
        soundOn={soundOn}
        micMuted={true}
        listenMuted={true}
        voiceEnabled={false}
        onToggleSound={() => setSoundOn((s) => !s)}
        onToggleMic={() => { /* n/a */ }}
        onToggleListen={() => { /* n/a */ }}
        onExit={() => { setSettingsOpen(false); navigate('/'); }}
        onClose={() => setSettingsOpen(false)}
        exitLabel="Exit to Home"
        anchor="left"
      />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@700;900&family=Poppins:wght@500;600;700;800&display=swap');
        .thimbles-root{font-family:'Poppins',system-ui,sans-serif}
        .thimbles-root .app-container{background:#1a0512}
        .cinzel{font-family:'Cinzel Decorative',serif}
        .gold-text{background:linear-gradient(180deg,#fff6cf,#ffd76a 38%,#f6a823 65%,#b9740d);-webkit-background-clip:text;background-clip:text;color:transparent;filter:drop-shadow(0 2px 1px rgba(0,0,0,.55))}
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes pulseGlow{0%,100%{box-shadow:0 0 0 0 rgba(255,196,60,.3)}50%{box-shadow:0 0 30px 4px rgba(255,196,60,.5)}}
        @keyframes rayspin{to{transform:rotate(360deg)}}
        @keyframes ringburst{0%{transform:scale(.3);opacity:.85}100%{transform:scale(2.6);opacity:0}}
        @keyframes shimmerText{0%,100%{filter:brightness(1) drop-shadow(0 2px 1px rgba(0,0,0,.55))}50%{filter:brightness(1.4) drop-shadow(0 0 10px rgba(255,200,90,.6))}}
        @keyframes loseShake{0%,100%{transform:translateX(0)}18%{transform:translateX(-9px)}36%{transform:translateX(8px)}54%{transform:translateX(-6px)}72%{transform:translateX(4px)}}
        @keyframes floatY{0%,100%{transform:translateY(0)}50%{transform:translateY(-7px)}}
        @keyframes hudSheen{0%{transform:translateX(-160%)}60%,100%{transform:translateX(360%)}}
        /* full-screen takeover: hide the site chrome while in-game */
        body.thimbles-active > #root > div > nav,
        body.thimbles-active [data-site-nav],
        body.thimbles-active [data-bottom-nav]{display:none !important}
      `}</style>

      {/* portrait phone-shaped frame — identical shell to every other game
          (max-width 430px on mobile, 9:19 card centred on desktop). */}
      <div className="app-container shadow-2xl text-white select-none">

      {/* ── background: CSS-generated stage (no image → instant) ──
          • WALL (top): a dark radial purple, glowing toward the horizon.
          • CARPET (bottom): a spotlight pool of warm purple where the board
            sits, fading to a dark floor. The HORIZON (wall↔carpet) is at ~46%,
            locked to the board. Colours sampled from the original art. */}
      <div className="absolute inset-0" style={{
        background:
          // spotlight pool on the carpet, lighting the board (dimmed magenta so
          // the stage sits clearly DARKER than the board for separation)
          'radial-gradient(80% 44% at 50% 56%, rgba(122,26,92,0.60), rgba(80,18,60,0) 72%),' +
          // soft wall glow rising toward the horizon
          'radial-gradient(98% 58% at 50% 40%, rgba(96,30,74,0.36), transparent 64%),' +
          // base vertical: dark magenta wall → darkened horizon → carpet → floor
          'linear-gradient(180deg,#100309 0%,#1b0511 22%,#330d26 40%,#511840 47%,#3c0f2e 64%,#100309 100%)',
      }} />
      {/* edge vignette for depth */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(135% 100% at 50% 48%, transparent 54%, rgba(0,0,0,0.5) 100%)' }} />

      {/* ── TOP BAR — STANDARDIZED LAYOUT, identical to every other game
          (Ludo / Plinko / Lucky7 / SL / Carrom / Chess / XO): two circular
          buttons on the left (Back, Settings) and a wallet chip on the right
          (coin + balance + recharge "+"). The notch is reserved via
          env(safe-area-inset-top). The ONLY difference from the others is the
          colour palette — Thimbles' MAGENTA (≈ #892b6c) bar with amber accents. ── */}
      <div
        className="flex justify-between items-center gap-2 px-3 sm:px-4 pb-3 sm:pb-4 bg-gradient-to-b from-[#5e1644] to-[#2a0a1e] shadow-md z-[150] w-full border-b border-amber-400/20 shrink-0"
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.75rem)' }}
      >
        {/* left cluster — Back + Settings (gap-2), same dimensions as the others */}
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => navigate('/')}
            aria-label="Back"
            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center bg-black/30 border border-white/15 hover:bg-black/50 active:scale-95 transition text-white"
          >
            <Icon name="arrow_back" size={22} filled />
          </button>
          <button
            onClick={() => setSettingsOpen((o) => !o)}
            aria-label="Settings"
            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center shadow-[0_0_5px_rgba(0,0,0,1)] hover:scale-105 active:scale-95 transition-transform"
          >
            <img src={IMG.settings} className="w-full h-full object-contain" alt="Settings" />
          </button>
        </div>

        {/* right — wallet chip (coin image + balance + recharge), same pattern */}
        <div className="bg-gradient-to-b from-[#511239] to-[#260a1b] rounded-lg shrink-0 flex items-center justify-center pe-1 sm:pe-[0.7rem] py-[0.3rem] sm:py-[0.5rem] border border-amber-400/25 shadow-[0_0_30px_rgba(246,168,35,0.08)] relative">
          <img src={IMG.coin} className="w-6 h-6 sm:w-7 sm:h-7 mr-2 relative scale-150 z-10" alt="Coin" />
          <span className="text-white font-bold text-base sm:text-lg mr-2 sm:mr-3 tracking-wide tabular-nums">{wallet.toLocaleString('en-IN')}</span>
          <button
            type="button"
            aria-label="Recharge wallet"
            onClick={() => navigate('/wallet')}
            className="rounded-sm flex items-center justify-center hover:scale-110 active:scale-95 transition-transform"
          >
            <img src={IMG.plus} className="w-6 h-6 sm:w-7 sm:h-7 object-contain" alt="Add" />
          </button>
        </div>
      </div>

      {/* transient error / maintenance toast (server messages: insufficient
          balance, round in progress, time's up, maintenance, …) */}
      <AnimatePresence>
        {notice && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            className="absolute left-1/2 -translate-x-1/2 z-[200] px-4 py-2 rounded-full text-[13px] font-bold text-rose-50 text-center max-w-[88%]"
            style={{ top: 'calc(env(safe-area-inset-top) + 4.2rem)', background: 'linear-gradient(180deg, rgba(150,30,50,0.96), rgba(90,16,32,0.96))', border: '1px solid rgba(255,140,150,0.5)', boxShadow: '0 6px 20px rgba(0,0,0,.5)' }}>
            {notice}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── STATUS RIBBON: an ornate gold-framed BLUE-VELVET banner — same
           material as the board (royal-blue velvet + gold filigree), with a
           gem on each end and the phase prompt in glowing gold serif. ── */}
      <div className="relative z-10 flex justify-center mt-2.5 px-4">
        <div className="relative rounded-full p-[2px]"
          style={{
            background: 'linear-gradient(180deg, #ffe9a8, #cfa53e 45%, #8a6410)',
            boxShadow: phase === 'picking'
              ? '0 0 22px 2px rgba(255,196,60,.5), 0 4px 10px rgba(0,0,0,.4)'
              : '0 5px 16px rgba(0,0,0,.45)',
          }}>
          <div className="relative flex items-center gap-2.5 rounded-full pl-3 pr-3 py-1.5 overflow-hidden"
            style={{ background: 'linear-gradient(180deg, #1c3f70 0%, #112a52 55%, #0a1c3a 100%)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,.18), inset 0 -4px 8px rgba(0,0,0,.45)' }}>
            <span className="absolute top-0 bottom-0 w-8 pointer-events-none"
              style={{ background: 'linear-gradient(100deg, transparent, rgba(255,255,255,.22), transparent)', animation: 'hudSheen 3.6s ease-in-out infinite' }} />
            <span className="w-2 h-2 rotate-45 shrink-0" style={{ background: 'linear-gradient(180deg,#ffe9a8,#d4a93f)', boxShadow: '0 0 5px rgba(255,200,90,.7)' }} />
            <div className="relative h-5 min-w-[136px] flex items-center justify-center overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.span key={hud.t} initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -12, opacity: 0 }} transition={{ duration: 0.22 }}
                  className="cinzel gold-text text-[15px] leading-none whitespace-nowrap tracking-wide"
                  style={phase === 'picking' ? { animation: 'shimmerText 1s ease-in-out infinite' } : undefined}>
                  {hud.t}
                </motion.span>
              </AnimatePresence>
            </div>
            <span className="w-2 h-2 rotate-45 shrink-0" style={{ background: 'linear-gradient(180deg,#ffe9a8,#d4a93f)', boxShadow: '0 0 5px rgba(255,200,90,.7)' }} />
          </div>
        </div>
      </div>

      {/* ═══ THE BOARD STAGE — width is container-relative so it fits the
          portrait phone frame on every device (no viewport-unit overflow). ═══ */}
      <div className="absolute inset-x-0 z-10 flex justify-center px-2" style={{ top: '42%' }}>
        <div className="relative" style={{ width: '100%', maxWidth: 440, aspectRatio: `${BOARD_AR}`, overflow: 'visible' }}>
          {/* the board art */}
          <img src={IMG.board} alt="" className="absolute inset-0 w-full h-full object-contain" style={{ filter: 'drop-shadow(0 24px 30px rgba(0,0,0,.55))' }} draggable={false} />

          {/* cups + ball on the velvet */}
          {cups.map((cup) => {
            const lifted = liftedIds.has(cup.id);
            const hasBall = ballUnderIds.has(cup.id);
            const isPicked = pickedId === cup.id;
            const clickable = phase === 'picking';
            // While shuffling, a moving cup ARCS around its partner: the front
            // cup curves forward-and-DOWN, the back cup curves up-and-BEHIND, the
            // crest hitting exactly at the crossover (times 0→0.5→1). Pure z-order
            // sells which is in front — no size change. The `tick`-based jot just
            // nudges the keyframe values so motion replays the arc every swap.
            const arcing = phase === 'shuffling' && cup.lane !== 'rest';
            const jot = tick % 2 ? 0.5 : 0;
            const crest = cup.lane === 'front' ? `${9 + jot}%` : `${-(7 + jot)}%`;
            const yAnim = arcing ? ['0%', crest, '0%'] : '0%';
            const laneZ = cup.lane === 'front' ? 50 : cup.lane === 'back' ? 12 : (lifted ? 35 : 22);
            const dur = swapMs / 1000;
            const moveT = phase === 'shuffling'
              ? { left: { duration: dur, ease: [0.42, 0, 0.58, 1] as const }, y: { duration: dur, ease: 'easeInOut' as const, times: [0, 0.5, 1] }, default: { duration: dur } }
              : { type: 'spring' as const, stiffness: 360, damping: 24, mass: 0.85 };
            return (
              <motion.div key={cup.id} className={`absolute ${clickable ? 'cursor-pointer' : ''}`}
                style={{ width: `${CUP_W * 100}%`, bottom: `${(1 - CUP_BASE_Y) * 100}%`, zIndex: laneZ }}
                animate={{ left: `${SLOT_X[cup.slot] * 100}%`, x: '-50%', y: yAnim }}
                transition={moveT}
                onClick={() => clickable && pick(cup)}
                whileHover={clickable ? { scale: 1.04 } : undefined} whileTap={clickable ? { scale: 0.97 } : undefined}>

                {/* the ball — ONLY mounted when this cup actually holds it (the
                    "watch" reveal or the final reveal). It is literally ABSENT
                    from the DOM during shuffle + picking, so it can't be found
                    by clearing the cups in dev-tools. It sits BEHIND the cup, so
                    the cup covers it on the way down (it never fades mid-air). */}
                {hasBall && (
                  <div className="absolute left-1/2 -translate-x-1/2" style={{ width: `${(BALL_W / CUP_W) * 100}%`, bottom: '0%' }}>
                    <Ball />
                  </div>
                )}

                {/* the cup + a glassy glint — they lift together. NO extra shadow
                    (the cup art carries its own bottom shadow) and NO vertical bob. */}
                <motion.div className="relative w-full"
                  animate={{ y: lifted ? LIFT_Y : '0%', rotate: lifted ? -9 : 0 }}
                  transition={{ type: 'spring', stiffness: 240, damping: 17 }}>
                  <img src={IMG.cup} alt="" draggable={false} className="block w-full" />
                  {/* polished-metal shine sweeping across, clipped to the cup silhouette */}
                  {(phase === 'idle' || clickable) && (
                    <div className="absolute inset-0 pointer-events-none overflow-hidden"
                      style={{ WebkitMaskImage: `url(${IMG.cup})`, WebkitMaskSize: '100% 100%', WebkitMaskRepeat: 'no-repeat',
                               maskImage: `url(${IMG.cup})`, maskSize: '100% 100%', maskRepeat: 'no-repeat', mixBlendMode: 'screen' }}>
                      <motion.div className="absolute top-[-10%] bottom-[-10%] w-[55%]"
                        style={{ background: 'linear-gradient(100deg, transparent, rgba(255,255,255,0.5), transparent)' }}
                        initial={{ left: '-65%' }} animate={{ left: '125%' }}
                        transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 2.6, ease: 'easeInOut', delay: cup.slot * 0.5 }} />
                    </div>
                  )}
                </motion.div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* ═══ controls ═══ */}
      <div className="absolute inset-x-0 bottom-0 z-10 px-4 pb-5 pt-5" style={{ background: 'linear-gradient(180deg, transparent, rgba(18,5,26,0.55) 26%, rgba(16,5,24,0.93) 60%)' }}>

        {/* ── mode selector: ornate gold-framed BLUE-VELVET plaques (same
             material as the board). Glossy red ball hero, gold COIN SEAL for the
             multiplier (echoes the win coin), gold serif title. Active = lit gold
             frame + warm glow + raised + floating balls + a gold ✓ seal. ── */}
        <div className="max-w-[460px] mx-auto grid grid-cols-2 gap-3 mb-4">
          {(['single', 'double'] as Mode[]).map((m) => {
            const active = mode === m;
            const balls = m === 'single' ? 1 : 2;
            return (
              <motion.button key={m} disabled={busy} onClick={() => { setMode(m); Sfx.tap(); }}
                animate={{ y: active ? -4 : 0 }} whileTap={busy ? undefined : { scale: 0.96 }}
                transition={{ type: 'spring', stiffness: 420, damping: 24 }}
                className={`relative rounded-2xl p-[2px] overflow-hidden ${busy ? 'opacity-60' : ''}`}
                style={{
                  background: active
                    ? 'linear-gradient(180deg, #ffe9a8, #cfa53e 45%, #8a6410)'
                    : 'linear-gradient(180deg, rgba(190,155,75,.45), rgba(80,58,22,.4))',
                  boxShadow: active ? '0 10px 26px rgba(246,168,35,.4)' : '0 4px 10px rgba(0,0,0,.4)',
                }}>
                {/* inner velvet face */}
                <div className="relative rounded-[14px] px-3 pt-2.5 pb-3 text-left overflow-hidden"
                  style={{
                    background: active
                      ? 'radial-gradient(120% 110% at 50% 0%, #2a64ac 0%, #143a72 52%, #0a2148 100%)'
                      : 'linear-gradient(180deg, #122c50, #081628)',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,.18), inset 0 -10px 18px rgba(0,0,0,.5)',
                  }}>
                  {/* glossy top sheen */}
                  <div className="absolute inset-x-0 top-0 h-1/2 pointer-events-none rounded-t-[14px]"
                    style={{ background: 'linear-gradient(180deg, rgba(255,255,255,.16), transparent)' }} />
                  {/* PICKED seal */}
                  {active && (
                    <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 500, damping: 18 }}
                      className="absolute top-2 right-2 grid place-items-center w-5 h-5 rounded-full z-10"
                      style={{ background: 'linear-gradient(180deg,#ffe89a,#f3a81f)', boxShadow: '0 1px 3px rgba(0,0,0,.5)' }}>
                      <Icon name="check" size={13} className="text-[#3a1d00]" filled />
                    </motion.span>
                  )}
                  {/* glossy red ball hero */}
                  <div className="relative flex items-end gap-1.5 h-9 mb-1.5" style={active ? { animation: 'floatY 2.4s ease-in-out infinite' } : undefined}>
                    {Array.from({ length: balls }).map((_, i) => (
                      <div key={i} className="w-8 h-8" style={{ filter: active ? 'drop-shadow(0 4px 6px rgba(255,70,50,.55))' : 'grayscale(.3) brightness(.7)' }}><Ball /></div>
                    ))}
                  </div>
                  {/* title */}
                  <div className={`relative cinzel text-[17px] leading-none ${active ? 'gold-text' : 'text-blue-100/65'}`}>{m === 'single' ? 'Single' : 'Double'}</div>
                  {/* odds + gold coin seal */}
                  <div className="relative flex items-end justify-between mt-2">
                    <span className="text-[9.5px] font-semibold tracking-wide leading-tight max-w-[60%]" style={{ color: active ? 'rgba(220,234,255,.75)' : 'rgba(200,215,255,.4)' }}>
                      {m === 'single' ? '1 in 3 · biggest win' : '2 in 3 · safer win'}
                    </span>
                    <div className="grid place-items-center rounded-full shrink-0" style={{
                      width: 42, height: 42,
                      background: active ? 'radial-gradient(circle at 42% 32%, #fff2c0, #f6c23a 46%, #e09a16 70%, #9c6406)' : 'radial-gradient(circle at 42% 32%, #6a6048, #4a432f 60%, #2c2818)',
                      boxShadow: active ? '0 3px 9px rgba(246,168,35,.45), inset 0 2px 3px rgba(255,255,255,.7), inset 0 -3px 6px rgba(130,80,0,.5)' : 'inset 0 1px 2px rgba(255,255,255,.18), inset 0 -2px 4px rgba(0,0,0,.5)',
                      border: active ? '2px solid rgba(255,236,170,.9)' : '2px solid rgba(150,140,100,.4)',
                    }}>
                      <span className="text-[12px] font-black tabular-nums leading-none" style={{ color: active ? '#3a1d00' : 'rgba(255,225,150,.6)' }}>{(cfg.multipliers[m] ?? MULT[m]).toFixed(2)}×</span>
                    </div>
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>

        {/* ── stake: real casino chips. Tap one to set your bet; the chosen chip
             physically lifts off the felt with a gold halo. No typing, no +/−. ── */}
        <div className="max-w-[430px] mx-auto grid gap-2 mb-3.5 pt-2.5"
             style={{ gridTemplateColumns: `repeat(${Math.max(1, cfg.presets.length)}, minmax(0,1fr))` }}>
          {cfg.presets.map((p, i) => {
            const active = bet === p;
            const tooMuch = p > wallet;
            const t = chipSkin(p, i);
            return (
              <motion.button key={p} disabled={busy || tooMuch} onClick={() => { setBet(p); Sfx.tap(); }}
                animate={{ y: active ? -9 : 0, scale: active ? 1.06 : 1 }}
                whileTap={busy || tooMuch ? undefined : { scale: 0.92 }}
                transition={{ type: 'spring', stiffness: 480, damping: 22 }}
                className="relative aspect-square rounded-full grid place-items-center disabled:opacity-45"
                style={{
                  background:
                    `radial-gradient(circle closest-side at 50% 30%, rgba(255,255,255,.45), transparent 56%),` +
                    `radial-gradient(circle closest-side, ${t.face} 0 70%, transparent 71%),` +
                    `radial-gradient(circle closest-side, transparent 0 88%, ${t.edge} 89%),` +
                    `repeating-conic-gradient(from 0deg, ${t.edge} 0 30deg, #fdf4e3 30deg 60deg)`,
                  boxShadow: active
                    ? '0 0 0 3px rgba(255,214,110,.95), 0 12px 22px rgba(255,180,40,.45), inset 0 2px 4px rgba(255,255,255,.4), inset 0 -3px 6px rgba(0,0,0,.4)'
                    : '0 6px 12px rgba(0,0,0,.5), inset 0 2px 4px rgba(255,255,255,.3), inset 0 -3px 6px rgba(0,0,0,.4)',
                  filter: tooMuch ? 'grayscale(.8) brightness(.7)' : active ? 'none' : 'brightness(.92) saturate(.95)',
                }}>
                <span className="absolute inset-[15%] rounded-full pointer-events-none" style={{ boxShadow: 'inset 0 0 0 1.5px rgba(255,255,255,.32)' }} />
                <span className="relative font-black tabular-nums leading-none"
                  style={{ color: t.ink, fontSize: p >= 1000 ? 12 : 14, textShadow: '0 1px 2px rgba(0,0,0,.55)' }}>
                  {p >= 1000 ? '1K' : p}
                </span>
              </motion.button>
            );
          })}
        </div>

        {/* ── PLAY: glossy gold bar with a live sheen + a recessed win readout ── */}
        <div className="max-w-[460px] mx-auto">
          <motion.button disabled={!canPlay} onClick={play} whileTap={canPlay ? { scale: 0.985 } : undefined}
            className="relative w-full h-16 rounded-2xl font-black overflow-hidden disabled:opacity-55 disabled:saturate-50"
            style={{ background: 'linear-gradient(180deg,#ffe89a,#f6b227 52%,#d07d0a)', color: '#3a1d00', boxShadow: '0 10px 26px rgba(246,168,35,.45), inset 0 1px 0 rgba(255,255,255,.75), inset 0 -3px 6px rgba(120,60,0,.32)' }}>
            {/* travelling sheen — only while playable */}
            {canPlay && (
              <motion.span className="absolute top-0 bottom-0 w-1/3 pointer-events-none"
                style={{ background: 'linear-gradient(100deg, transparent, rgba(255,255,255,.55), transparent)' }}
                initial={{ left: '-40%' }} animate={{ left: '130%' }}
                transition={{ duration: 1.8, repeat: Infinity, repeatDelay: 1.5, ease: 'easeInOut' }} />
            )}
            {busy ? (
              <span className="relative flex items-center justify-center gap-2 text-lg tracking-wide">
                {phase === 'picking'
                  ? <>Pick a cup <span className="text-xl">↑</span></>
                  : <><span className="inline-block w-4 h-4 border-2 border-[#3a1d00]/40 border-t-[#3a1d00] rounded-full" style={{ animation: 'spin .7s linear infinite' }} /> Shuffling…</>}
              </span>
            ) : (
              <span className="relative flex items-center justify-between px-5">
                <span className="flex items-center gap-2">
                  <span className="grid place-items-center w-8 h-8 rounded-full" style={{ background: 'rgba(58,29,0,.16)', boxShadow: 'inset 0 1px 1px rgba(255,255,255,.4)' }}>
                    <Icon name="play_arrow" size={22} />
                  </span>
                  <span className="text-xl tracking-[0.12em]">PLAY</span>
                </span>
                <span className="flex flex-col items-end leading-none px-3 py-1 rounded-xl" style={{ background: 'rgba(58,29,0,.18)', boxShadow: 'inset 0 1px 2px rgba(0,0,0,.25)' }}>
                  <span className="text-[8.5px] font-extrabold tracking-[0.25em] opacity-70">YOU WIN</span>
                  <span className="text-[17px] font-black tabular-nums mt-0.5">{winChips.toLocaleString('en-IN')}</span>
                </span>
              </span>
            )}
          </motion.button>
        </div>
      </div>

      {/* ═══ BET TIMER — slides UP over the control deck the instant the bet is
          placed, covering the buttons. 45s to pick a cup; on 0 the round is
          forfeited (bet lost). Sits above the controls (z-30), below result. ═══ */}
      <AnimatePresence>
        {showTimer && (
          <motion.div key="bettimer" initial={{ y: '101%' }} animate={{ y: 0 }} exit={{ y: '101%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 32 }}
            className="absolute inset-x-0 bottom-0 z-30 px-5 pt-7 pb-8"
            style={{ background: 'linear-gradient(180deg, rgba(16,5,24,0) 0%, rgba(16,5,24,0.94) 28%, rgba(11,3,18,0.99) 54%)' }}>
            <div className="max-w-[460px] mx-auto flex flex-col items-center gap-3">
              <div className="relative w-28 h-28">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="44" fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="7" />
                  <circle cx="50" cy="50" r="44" fill="none" strokeLinecap="round" strokeWidth="7"
                    stroke={urgent ? '#ff5d5d' : '#f6b227'}
                    strokeDasharray={276.46} strokeDashoffset={276.46 * (1 - timeLeft / cfg.pickTimeoutSeconds)}
                    style={{ transition: 'stroke-dashoffset 1s linear, stroke .3s' }} />
                </svg>
                <div className="absolute inset-0 grid place-items-center">
                  <div className="flex flex-col items-center leading-none">
                    <motion.span key={timeLeft} initial={{ scale: 1.25, opacity: 0.45 }} animate={{ scale: 1, opacity: 1 }}
                      className={`text-[40px] font-black tabular-nums ${urgent ? 'text-red-300' : 'text-amber-100'}`}
                      style={urgent ? { animation: 'pulseGlow 1s ease-in-out infinite' } : undefined}>{timeLeft}</motion.span>
                    <span className="text-[9px] tracking-[0.32em] text-white/45 mt-1">SECONDS</span>
                  </div>
                </div>
              </div>
              <p className={`text-base font-extrabold tracking-wide text-center ${phase === 'picking' ? 'gold-text' : 'text-amber-100/85'}`}>{timerMsg}</p>
              <p className="text-[10.5px] text-white/45 text-center -mt-1">Choose a cup before the timer ends, or your bet is lost</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ RESULT — a full, juicy win / lose moment ═══ */}
      <AnimatePresence>
        {phase === 'result' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 z-40 flex items-center justify-center pointer-events-none overflow-hidden"
            style={{
              background: won
                ? 'radial-gradient(circle at 50% 42%, rgba(80,40,8,0.55), rgba(8,3,14,0.86) 70%)'
                : 'radial-gradient(circle at 50% 42%, rgba(60,12,28,0.5), rgba(6,2,10,0.88) 72%)',
              backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)',
            }}>

            {won ? (
              <>
                {/* spinning light rays */}
                <div className="absolute" style={{
                  width: 560, height: 560, animation: 'rayspin 14s linear infinite',
                  background: 'repeating-conic-gradient(from 0deg, rgba(255,214,120,.20) 0 7deg, transparent 7deg 19deg)',
                  WebkitMaskImage: 'radial-gradient(circle, #000 6%, transparent 60%)', maskImage: 'radial-gradient(circle, #000 6%, transparent 60%)',
                }} />
                {/* entry burst ring */}
                <div className="absolute rounded-full" style={{ width: 220, height: 220, border: '3px solid rgba(255,214,120,.7)', animation: 'ringburst .9s ease-out forwards' }} />
                {/* coin + sparkle shower */}
                <div className="absolute inset-0 overflow-hidden">
                  {Array.from({ length: 34 }).map((_, i) => (
                    <motion.span key={i} className="absolute" initial={{ y: -50, opacity: 1, rotate: 0 }} animate={{ y: '115%', rotate: 520, opacity: 0 }}
                      transition={{ duration: 1.8 + (i % 5) * 0.25, ease: 'easeIn', delay: (i % 7) * 0.08, repeat: Infinity, repeatDelay: 0.3 }}
                      style={{ left: `${(i * 3.1 + 4) % 100}%`, fontSize: 15 + (i % 4) * 6 }}>{['🪙', '✨', '💰', '⭐'][i % 4]}</motion.span>
                  ))}
                </div>
                {/* center stack */}
                <motion.div initial={{ scale: 0.4, opacity: 0, y: 14 }} animate={{ scale: 1, opacity: 1, y: 0 }}
                  transition={{ type: 'spring', stiffness: 320, damping: 16 }} className="relative flex flex-col items-center px-8">
                  <div className="cinzel text-4xl gold-text leading-none" style={{ animation: 'shimmerText 1.4s ease-in-out infinite' }}>YOU WON</div>
                  {/* big gold coin badge with the counting payout */}
                  <div className="relative mt-4 grid place-items-center" style={{ animation: 'floatY 2.6s ease-in-out infinite' }}>
                    <div className="grid place-items-center rounded-full" style={{
                      width: 150, height: 150,
                      background: 'radial-gradient(circle at 42% 32%, #fff2c0, #f6c23a 46%, #e09a16 70%, #9c6406)',
                      boxShadow: '0 12px 34px rgba(246,168,35,.5), inset 0 3px 6px rgba(255,255,255,.7), inset 0 -8px 16px rgba(130,80,0,.5)',
                      border: '4px solid rgba(255,236,170,.9)',
                    }}>
                      <div className="flex flex-col items-center leading-none">
                        <span className="text-[11px] font-extrabold tracking-[0.2em] text-[#7a4d00]">WON</span>
                        <span className="text-[34px] font-black tabular-nums text-[#3a1d00] mt-0.5">+{shownPayout.toLocaleString('en-IN')}</span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 text-[11px] uppercase tracking-[0.3em] text-amber-200/70">{mode} ball · {mult.toFixed(2)}×</div>
                </motion.div>
              </>
            ) : (
              <>
                {/* cold spotlight rays sweeping the other way */}
                <div className="absolute" style={{
                  width: 520, height: 520, animation: 'rayspin 20s linear infinite reverse',
                  background: 'repeating-conic-gradient(from 0deg, rgba(170,195,255,.10) 0 8deg, transparent 8deg 22deg)',
                  WebkitMaskImage: 'radial-gradient(circle, #000 6%, transparent 58%)', maskImage: 'radial-gradient(circle, #000 6%, transparent 58%)',
                }} />
                <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 280, damping: 20 }}
                  className="relative flex flex-col items-center px-8" style={{ animation: 'loseShake .55s ease-out' }}>
                  {/* the BALL is revealed — it drops in under the spotlight to show
                      where it was hiding (the hero element, big and glossy). */}
                  <motion.div initial={{ y: -130, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ type: 'spring', stiffness: 360, damping: 13, delay: 0.05 }}
                    className="w-24 h-24 relative z-10">
                    <Ball />
                  </motion.div>
                  <div className="cinzel text-4xl mt-3 leading-none" style={{ color: '#ffdbe4', textShadow: '0 2px 6px rgba(255,80,120,.5), 0 0 18px rgba(255,60,100,.4)', animation: 'shimmerText 1.6s ease-in-out infinite' }}>
                    {timedOut ? "TIME'S UP!" : 'SO CLOSE!'}
                  </div>
                  {/* short subtitle in warm cream (not dull gray) */}
                  <div className="mt-2 text-[15px] font-semibold tracking-wide text-center" style={{ color: '#f1d5cf' }}>
                    {timedOut ? 'You ran out of time' : 'It was hiding under this cup'}
                  </div>
                  {/* encouragement as an ornate gold-framed BLUE-VELVET ribbon —
                      the same material as the HUD strip + mode plaques. */}
                  <div className="mt-4 relative rounded-full p-[2px]" style={{ background: 'linear-gradient(180deg, #ffe9a8, #cfa53e 45%, #8a6410)', boxShadow: '0 6px 18px rgba(0,0,0,.5)' }}>
                    <div className="flex items-center gap-2 rounded-full px-5 py-2" style={{ background: 'linear-gradient(180deg, #1c3f70 0%, #112a52 55%, #0a1c3a 100%)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,.18), inset 0 -4px 8px rgba(0,0,0,.45)' }}>
                      <span className="text-lg leading-none">🍀</span>
                      <span className="cinzel gold-text text-[15px] tracking-wide leading-none">Next round is yours</span>
                    </div>
                  </div>
                  {/* mode chip */}
                  <div className="mt-4 px-3 py-1 rounded-full text-[9px] font-bold uppercase tracking-[0.3em]"
                    style={{ background: 'rgba(0,0,0,.32)', border: '1px solid rgba(255,205,110,.28)', color: 'rgba(255,221,150,.72)' }}>
                    {mode} ball · {mult.toFixed(2)}×
                  </div>
                </motion.div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
      </div>{/* /.app-container */}
    </div>
  );
}
